// src/components/auction/AuctionAnalysisDashboard.js
// Pannelli della dashboard "Analisi asta": striscia KPI di lega, card record,
// due grafici recharts (fantamedia prevista / spesa per reparto) e la
// tabella di dettaglio per reparto. Tutti i pezzi lavorano sui dati già
// pronti da buildTeamRosterStats/buildTeamInsightDetail (auctionAnalysis.js):
// nessuna nuova funzione di analisi, solo derivazioni con map/sort/reduce.
import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { Crown, TrendingUp, TrendingDown, Wallet, Layers, Scale, Dice5, Coins, ShieldCheck, ExternalLink, CalendarDays, AlertTriangle, Award } from 'lucide-react';
import TeamBadge from '../TeamBadge';
import AppTooltip from '../Tooltip';
import { useTheme } from '../../context/ThemeContext';
import { buildTeamInsightDetail } from './auctionAnalysis';
import { calcPlayerScore, fmt, v, RoleChip, AuctionTeamAvatar, ROLE_ORDER, ROLE_LABELS, ROLE_CHIP_THEME } from './auctionCommon';
import {
  getUpcomingFixturesWithDifficulty,
  formatFixtureDate,
  formatDifficultyValue,
  getDifficultyColor,
} from '../../data/serieACalendar';

function getPurchaseName(purchase) {
  return purchase?.playerSnapshot?.name || purchase?.playerName || '—';
}

function getPurchaseTeam(purchase) {
  return purchase?.playerSnapshot?.team || purchase?.playerTeam || '—';
}

function openPlayerDetailNewTab(playerId) {
  window.open(`${window.location.origin}/players/${playerId}`, '_blank', 'noopener,noreferrer');
}

// ── Striscia KPI di lega ─────────────────────────────────────────────────

export function AnalysisKpiStrip({ teamStats = [], rankedTeams = [] }) {
  const kpis = useMemo(() => {
    if (!teamStats.length) return null;

    const leader = rankedTeams[0] || null;

    const predictedFvs = teamStats
      .map((t) => buildTeamInsightDetail(t)?.predictedFv)
      .filter((n) => Number.isFinite(n));
    const avgFv = predictedFvs.length ? predictedFvs.reduce((a, b) => a + b, 0) / predictedFvs.length : 0;

    const totalSpent = teamStats.reduce((acc, t) => acc + v(t.spent), 0);

    const spentByRoleLeague = { P: 0, D: 0, C: 0, A: 0 };
    teamStats.forEach((t) => {
      t.roleBreakdown.forEach((r) => {
        spentByRoleLeague[r.role] = (spentByRoleLeague[r.role] || 0) + v(r.spent);
      });
    });
    const priciestRole = ROLE_ORDER.reduce(
      (best, role) => (spentByRoleLeague[role] > (spentByRoleLeague[best] || 0) ? role : best),
      ROLE_ORDER[0]
    );

    return { leader, avgFv, totalSpent, priciestRole, priciestRoleSpent: spentByRoleLeague[priciestRole] };
  }, [teamStats, rankedTeams]);

  if (!kpis) return null;

  return (
    <div className="analysis-kpi-strip">
      <div className="analysis-kpi">
        <Crown size={18} />
        <div>
          <span>Squadra leader</span>
          <AppTooltip as="strong" content={kpis.leader?.team}>{kpis.leader?.team || '—'}</AppTooltip>
        </div>
      </div>
      <div className="analysis-kpi">
        <TrendingUp size={18} />
        <div>
          <span>Fantamedia media di lega</span>
          <strong>{fmt(kpis.avgFv)}</strong>
        </div>
      </div>
      <div className="analysis-kpi">
        <Wallet size={18} />
        <div>
          <span>Spesa totale in asta</span>
          <strong>{kpis.totalSpent} cr</strong>
        </div>
      </div>
      <div className="analysis-kpi">
        <Layers size={18} />
        <div>
          <span>Reparto più costoso</span>
          <strong>{ROLE_LABELS[kpis.priciestRole]} · {kpis.priciestRoleSpent} cr</strong>
        </div>
      </div>
    </div>
  );
}

// ── Card record ───────────────────────────────────────────────────────────

export function AnalysisAwardCards({ teamStats = [], onOpenTeamDetail }) {
  const awards = useMemo(() => {
    if (!teamStats.length) return null;

    const byEfficiency = [...teamStats].sort((a, b) => b.efficiency - a.efficiency)[0];
    const byCompleteness = [...teamStats].sort((a, b) => b.completeness - a.completeness)[0];

    let bestBargainTeam = null;
    let bestBargainPlayer = null;
    let bestBargainScore = -Infinity;
    teamStats.forEach((t) => {
      const bargain = buildTeamInsightDetail(t)?.bargainPlayer;
      if (!bargain) return;
      const score = calcPlayerScore(bargain);
      if (score > bestBargainScore) {
        bestBargainScore = score;
        bestBargainPlayer = bargain;
        bestBargainTeam = t;
      }
    });

    let priciestTeam = null;
    let priciestPlayer = null;
    teamStats.forEach((t) => {
      if (t.topSpendPlayer && (!priciestPlayer || v(t.topSpendPlayer.credits) > v(priciestPlayer.credits))) {
        priciestPlayer = t.topSpendPlayer;
        priciestTeam = t;
      }
    });

    return { byEfficiency, byCompleteness, bestBargainTeam, bestBargainPlayer, priciestTeam, priciestPlayer };
  }, [teamStats]);

  if (!awards) return null;

  return (
    <div className="analysis-award-grid">
      <button type="button" className="analysis-award-card" onClick={() => onOpenTeamDetail(awards.byEfficiency)}>
        <Scale size={16} />
        <span className="analysis-award-card__label">Miglior rapporto qualità/prezzo</span>
        <strong>{awards.byEfficiency.team}</strong>
        <span className="analysis-award-card__value">{fmt(awards.byEfficiency.efficiency)} pt/cr</span>
      </button>

      {awards.bestBargainPlayer ? (
        <button type="button" className="analysis-award-card" onClick={() => onOpenTeamDetail(awards.bestBargainTeam)}>
          <Dice5 size={16} />
          <span className="analysis-award-card__label">Colpo dell'asta</span>
          <strong>{getPurchaseName(awards.bestBargainPlayer)}</strong>
          <span className="analysis-award-card__value">{awards.bestBargainTeam?.team} · {awards.bestBargainPlayer.credits} cr</span>
        </button>
      ) : (
        <div className="analysis-award-card analysis-award-card--empty">
          <Dice5 size={16} />
          <span className="analysis-award-card__label">Colpo dell'asta</span>
          <span className="mm-muted">Nessun giocatore pagato 1 credito.</span>
        </div>
      )}

      {awards.priciestPlayer && (
        <button type="button" className="analysis-award-card" onClick={() => onOpenTeamDetail(awards.priciestTeam)}>
          <Coins size={16} />
          <span className="analysis-award-card__label">Giocatore più pagato</span>
          <strong>{getPurchaseName(awards.priciestPlayer)}</strong>
          <span className="analysis-award-card__value">{awards.priciestTeam?.team} · {awards.priciestPlayer.credits} cr</span>
        </button>
      )}

      <button type="button" className="analysis-award-card" onClick={() => onOpenTeamDetail(awards.byCompleteness)}>
        <ShieldCheck size={16} />
        <span className="analysis-award-card__label">Rosa più completa</span>
        <strong>{awards.byCompleteness.team}</strong>
        <span className="analysis-award-card__value">{Math.round(awards.byCompleteness.completeness * 100)}%</span>
      </button>
    </div>
  );
}

// ── Grafici (recharts, stesso pattern di TrendChart in ScambiCommon.js) ───

function ChartTooltip({ active, payload, label, isDark, formatValue }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip" style={{ background: isDark ? '#0f172a' : '#ffffff', color: isDark ? '#f8fafc' : '#111827' }}>
      <strong className="chart-tooltip__label">{label}</strong>
      {payload.map((item) => (
        <div key={item.dataKey} className="chart-tooltip__row">
          <span className="chart-tooltip__dot" style={{ background: item.color }} />
          {item.name}: <strong>{formatValue ? formatValue(item.value) : item.value}</strong>
        </div>
      ))}
    </div>
  );
}

// Nomi squadra ruotati (invece che troncati a poche lettere in orizzontale):
// molti fantallenatori usano nomi lunghi/fantasiosi, troncare a 8-9 caratteri
// li rendeva illeggibili. In diagonale entra molto più testo leggibile.
function TeamAxisTick({ x, y, payload, myTeamName, fill }) {
  const isMine = payload.value === myTeamName;
  const label = payload.value.length > 18 ? `${payload.value.slice(0, 17)}…` : payload.value;
  return (
    <text
      x={x}
      y={y}
      dy={9}
      textAnchor="end"
      transform={`rotate(-35 ${x} ${y})`}
      fontSize={11.5}
      fontWeight={isMine ? 900 : 500}
      fill={isMine ? '#7c5cff' : fill}
    >
      {label}
    </text>
  );
}

export function PredictedFvChart({ teamStats = [], myTeamName }) {
  const { isDark } = useTheme();
  const chartText = isDark ? '#e5e7eb' : '#111827';
  const gridColor = isDark ? 'rgba(255,255,255,.10)' : 'rgba(17,24,39,.10)';

  const data = useMemo(
    () => teamStats
      .map((t) => ({ team: t.team, predictedFv: buildTeamInsightDetail(t)?.predictedFv || 0, isMine: t.team === myTeamName }))
      .sort((a, b) => b.predictedFv - a.predictedFv),
    [teamStats, myTeamName]
  );

  const minWidth = Math.max(data.length * 80, 280);

  return (
    <div className="analysis-chart-panel glass">
      <div className="analysis-chart-panel__head">
        <TrendingUp size={16} />
        <div>
          <strong>Fantamedia prevista per squadra</strong>
          <span>Media dei fantavoti attesi su tutta la rosa</span>
        </div>
      </div>
      <div className="analysis-chart-panel__scroll">
        <div style={{ minWidth }}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data} margin={{ top: 10, right: 8, left: 0, bottom: 12 }}>
              <CartesianGrid stroke={gridColor} strokeDasharray="4 4" vertical={false} />
              <XAxis dataKey="team" tick={<TeamAxisTick myTeamName={myTeamName} fill={chartText} />} interval={0} tickLine={false} height={64} />
              <YAxis tick={{ fill: chartText, fontSize: 11 }} width={40} />
              <Tooltip content={<ChartTooltip isDark={isDark} formatValue={(val) => fmt(val)} />} cursor={{ fill: 'rgba(124,92,255,0.06)' }} />
              <Bar dataKey="predictedFv" name="Fantamedia prevista" radius={[6, 6, 0, 0]}>
                {data.map((entry) => (
                  <Cell key={entry.team} fill={entry.isMine ? '#7c5cff' : '#31d0aa'} fillOpacity={entry.isMine ? 1 : 0.75} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export function RoleSpendChart({ teamStats = [], myTeamName }) {
  const { isDark } = useTheme();
  const chartText = isDark ? '#e5e7eb' : '#111827';
  const gridColor = isDark ? 'rgba(255,255,255,.10)' : 'rgba(17,24,39,.10)';

  const data = useMemo(() => teamStats.map((t) => {
    const row = { team: t.team };
    t.roleBreakdown.forEach((r) => { row[r.role] = v(r.spent); });
    return row;
  }), [teamStats]);

  const minWidth = Math.max(data.length * 80, 280);

  return (
    <div className="analysis-chart-panel glass">
      <div className="analysis-chart-panel__head">
        <Layers size={16} />
        <div>
          <strong>Spesa per reparto per squadra</strong>
          <span>Come ciascun partecipante ha distribuito i crediti tra i reparti</span>
        </div>
      </div>
      <div className="analysis-chart-panel__scroll">
        <div style={{ minWidth }}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data} margin={{ top: 10, right: 8, left: 0, bottom: 12 }}>
              <CartesianGrid stroke={gridColor} strokeDasharray="4 4" vertical={false} />
              <XAxis dataKey="team" tick={<TeamAxisTick myTeamName={myTeamName} fill={chartText} />} interval={0} tickLine={false} height={64} />
              <YAxis tick={{ fill: chartText, fontSize: 11 }} width={46} />
              <Tooltip content={<ChartTooltip isDark={isDark} formatValue={(val) => `${val} cr`} />} cursor={{ fill: 'rgba(124,92,255,0.06)' }} />
              <Legend wrapperStyle={{ color: chartText, fontSize: 12 }} />
              {ROLE_ORDER.map((role, idx) => (
                <Bar
                  key={role}
                  dataKey={role}
                  name={ROLE_LABELS[role]}
                  stackId="spend"
                  fill={ROLE_CHIP_THEME[role].backgroundColor}
                  radius={idx === ROLE_ORDER.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// ── Tabella dettaglio per reparto ─────────────────────────────────────────

export function AnalysisRoleTable({ teamStats = [], onOpenTeamDetail }) {
  const [activeRole, setActiveRole] = useState('D');

  const rows = useMemo(() => teamStats
    .map((t) => {
      const roleData = t.roleBreakdown.find((r) => r.role === activeRole);
      if (!roleData) return null;
      const topPlayer = roleData.players.length
        ? [...roleData.players].sort((a, b) => v(b.credits) - v(a.credits))[0]
        : null;
      return {
        team: t.team,
        teamStat: t,
        owned: roleData.owned,
        spent: roleData.spent,
        avgSpent: roleData.owned ? roleData.spent / roleData.owned : 0,
        topPlayer,
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.spent - a.spent), [teamStats, activeRole]);

  return (
    <div className="analysis-stats-table-panel glass">
      <div className="analysis-chart-panel__head">
        <Layers size={16} />
        <div>
          <strong>Spesa per reparto, squadra per squadra</strong>
          <span>Speso totale, spesa media e giocatore più costoso per ciascun reparto</span>
        </div>
      </div>

      <div className="analysis-role-pills">
        {ROLE_ORDER.map((role) => (
          <button
            key={role}
            type="button"
            className={`analysis-role-pill${activeRole === role ? ' is-active' : ''}`}
            onClick={() => setActiveRole(role)}
          >
            <RoleChip role={role} />
            {ROLE_LABELS[role]}
          </button>
        ))}
      </div>

      <div className="analysis-stats-table__scroll">
        <table className="analysis-stats-table">
          <thead>
            <tr>
              <th>Squadra</th>
              <th>Giocatori</th>
              <th>Speso totale</th>
              <th>Spesa media</th>
              <th>Giocatore più costoso</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.team} className="analysis-stats-table__row" onClick={() => onOpenTeamDetail(row.teamStat)}>
                <td>
                  <div className="analysis-stats-table__team">
                    <AuctionTeamAvatar name={row.team} />
                    <span>{row.team}</span>
                  </div>
                </td>
                <td>{row.owned}</td>
                <td>{row.spent} cr</td>
                <td>{fmt(row.avgSpent)} cr</td>
                <td>
                  {row.topPlayer ? (
                    <div className="analysis-stats-table__player">
                      <TeamBadge team={getPurchaseTeam(row.topPlayer)} size={22} />
                      <div className="analysis-stats-table__playerBody">
                        <span>{getPurchaseName(row.topPlayer)}</span>
                        <span className="analysis-stats-table__playerCredits">{row.topPlayer.credits} cr</span>
                      </div>
                      <AppTooltip
                        as="button"
                        type="button"
                        className="analysis-stats-table__playerBtn"
                        onClick={(e) => { e.stopPropagation(); openPlayerDetailNewTab(row.topPlayer.playerId); }}
                        content="Apri dettaglio giocatore"
                      >
                        <ExternalLink size={12} />
                      </AppTooltip>
                    </div>
                  ) : (
                    <span className="mm-muted">—</span>
                  )}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={5} className="analysis-stats-table__empty">Nessun giocatore in questo ruolo.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Punti di forza e di debolezza della rosa ────────────────────────────────
// Nessun punteggio inventato: solo dati già calcolati altrove (copertura
// titolari da roleBreakdown, fantamedia prevista per ruolo da
// buildTeamInsightDetail) riletti reparto per reparto per capire dove la
// rosa è più scoperta/profonda e dove rende di più/di meno.

export function AnalysisRosterStrengths({ teamStats = [], myTeamName }) {
  const myTeam = useMemo(
    () => teamStats.find((t) => t.team === myTeamName) || null,
    [teamStats, myTeamName]
  );
  const detail = useMemo(() => buildTeamInsightDetail(myTeam), [myTeam]);

  const roleRows = useMemo(() => {
    if (!myTeam) return [];
    return myTeam.roleBreakdown.map((r) => {
      const fvRole = detail?.predictedFvByRole.find((x) => x.role === r.role);
      return {
        role: r.role,
        owned: r.owned,
        needed: r.needed,
        deficit: r.deficit,
        spent: r.spent,
        predictedFv: fvRole && fvRole.owned ? fvRole.predictedFv : null,
      };
    });
  }, [myTeam, detail]);

  if (!myTeam || !roleRows.length) return null;

  const totalSpent = roleRows.reduce((acc, r) => acc + r.spent, 0);

  const mostLacking = [...roleRows].sort((a, b) => b.deficit - a.deficit)[0];
  const hasGaps = mostLacking && mostLacking.deficit > 0;

  const deepestRole = [...roleRows].sort((a, b) => (b.owned - b.needed) - (a.owned - a.needed))[0];

  const roleWithFv = roleRows.filter((r) => r.predictedFv != null);
  const strongestRole = roleWithFv.length ? [...roleWithFv].sort((a, b) => b.predictedFv - a.predictedFv)[0] : null;
  const weakestRole = roleWithFv.length > 1 ? [...roleWithFv].sort((a, b) => a.predictedFv - b.predictedFv)[0] : null;

  return (
    <div className="analysis-stats-table-panel glass">
      <div className="analysis-chart-panel__head">
        <ShieldCheck size={16} />
        <div>
          <strong>Punti di forza e di debolezza della rosa</strong>
          <span>Copertura titolari e fantamedia prevista, reparto per reparto, per {myTeam.team}</span>
        </div>
      </div>

      <div className="roster-strengths-callouts">
        <div className={`roster-strength-callout${hasGaps ? ' is-warning' : ' is-positive'}`}>
          <AlertTriangle size={16} />
          <div>
            <span>Reparto più scoperto</span>
            <strong>
              {hasGaps
                ? `${ROLE_LABELS[mostLacking.role]} · mancano ${mostLacking.deficit} titolari`
                : 'Nessuna lacuna: tutti i titolari coperti'}
            </strong>
          </div>
        </div>

        <div className="roster-strength-callout">
          <Layers size={16} />
          <div>
            <span>Reparto con più profondità</span>
            <strong>{ROLE_LABELS[deepestRole.role]} · {deepestRole.owned}/{deepestRole.needed} titolari</strong>
          </div>
        </div>

        {strongestRole && (
          <div className="roster-strength-callout is-positive">
            <TrendingUp size={16} />
            <div>
              <span>Punto di forza (fantamedia prevista)</span>
              <strong>{ROLE_LABELS[strongestRole.role]} · {fmt(strongestRole.predictedFv)}</strong>
            </div>
          </div>
        )}

        {weakestRole && weakestRole.role !== strongestRole?.role && (
          <div className="roster-strength-callout is-warning">
            <TrendingDown size={16} />
            <div>
              <span>Punto debole (fantamedia prevista)</span>
              <strong>{ROLE_LABELS[weakestRole.role]} · {fmt(weakestRole.predictedFv)}</strong>
            </div>
          </div>
        )}
      </div>

      <div className="roster-strengths-grid">
        {roleRows.map((r) => (
          <div key={r.role} className="roster-strength-role">
            <div className="roster-strength-role__head">
              <RoleChip role={r.role} />
              <span>{ROLE_LABELS[r.role]}</span>
            </div>
            <div className="roster-strength-role__bar">
              <div
                className={`roster-strength-role__barFill${r.deficit > 0 ? ' is-deficit' : ''}`}
                style={{ width: `${Math.min(100, (r.needed ? r.owned / r.needed : (r.owned ? 1 : 0)) * 100)}%` }}
              />
            </div>
            <div className="roster-strength-role__stats">
              <span>{r.owned}/{r.needed} titolari{r.deficit > 0 ? ` · mancano ${r.deficit}` : ''}</span>
              <span>{r.predictedFv != null ? `FV prevista ${fmt(r.predictedFv)}` : 'FV prevista n/d'}</span>
              <span>{r.spent} cr{totalSpent ? ` · ${Math.round((r.spent / totalSpent) * 100)}% del budget speso` : ''}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Occasioni e rimpianti: com'è andata la tua asta ─────────────────────────
// Nessun nuovo giudizio calcolato: confronta solo prezzo pagato (già
// registrato) e rendimento reale (calcPlayerScore, lo stesso usato ovunque
// nell'app per prezzare/valutare), sulla TUA rosa. "Da rivedere" esclude i
// riempitivi da pochi crediti: un 1-2 crediti che rende poco non è un
// rimpianto, è un rischio a costo quasi nullo che si accetta di default.
const DRAFT_REPORT_MIN_BUST_CREDITS = 3;

export function AnalysisDraftReport({ teamStats = [], myTeamName }) {
  const myTeam = useMemo(
    () => teamStats.find((t) => t.team === myTeamName) || null,
    [teamStats, myTeamName]
  );

  const ranked = useMemo(() => {
    if (!myTeam) return [];
    return myTeam.roster.map((purchase) => {
      const credits = v(purchase.credits);
      const score = calcPlayerScore(purchase);
      return { purchase, credits, score, ratio: score / Math.max(credits, 1) };
    });
  }, [myTeam]);

  if (!myTeam || ranked.length < 4) return null;

  const bargains = [...ranked].sort((a, b) => b.ratio - a.ratio).slice(0, 3);
  const busts = [...ranked]
    .filter((r) => r.credits >= DRAFT_REPORT_MIN_BUST_CREDITS)
    .sort((a, b) => a.ratio - b.ratio)
    .slice(0, 3);

  return (
    <div className="analysis-stats-table-panel glass">
      <div className="analysis-chart-panel__head">
        <Award size={16} />
        <div>
          <strong>Occasioni e rimpianti</strong>
          <span>Prezzo pagato contro rendimento reale per {myTeam.team}: com'è andata davvero la tua asta</span>
        </div>
      </div>

      <div className="draft-report-grid">
        <div className="draft-report-col">
          <span className="draft-report-col__title draft-report-col__title--good">
            <TrendingUp size={13} /> Migliori colpi
          </span>
          {bargains.map(({ purchase, credits, score }) => (
            <div key={purchase.playerId ?? purchase.id} className="draft-report-row">
              <TeamBadge team={getPurchaseTeam(purchase)} size={26} />
              <div className="draft-report-row__info">
                <strong>{getPurchaseName(purchase)}</strong>
                <span>{credits} cr pagati · rendimento {fmt(score)}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="draft-report-col">
          <span className="draft-report-col__title draft-report-col__title--bad">
            <TrendingDown size={13} /> Da rivedere
          </span>
          {busts.length ? busts.map(({ purchase, credits, score }) => (
            <div key={purchase.playerId ?? purchase.id} className="draft-report-row">
              <TeamBadge team={getPurchaseTeam(purchase)} size={26} />
              <div className="draft-report-row__info">
                <strong>{getPurchaseName(purchase)}</strong>
                <span>{credits} cr pagati · rendimento {fmt(score)}</span>
              </div>
            </div>
          )) : (
            <span className="draft-report-empty">Nessun acquisto costoso sotto le attese: buona asta.</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Calendario della rosa (giocatore per giocatore) ────────────────────────

export function AnalysisRosterCalendar({ teamStats = [], myTeamName }) {
  const myTeam = useMemo(
    () => teamStats.find((t) => t.team === myTeamName) || null,
    [teamStats, myTeamName]
  );

  const rows = useMemo(() => {
    if (!myTeam) return [];
    return myTeam.roleBreakdown.flatMap((roleData) =>
      [...roleData.players]
        .sort((a, b) => v(b.credits) - v(a.credits))
        .map((purchase) => {
          const team = getPurchaseTeam(purchase);
          const fixtures = getUpcomingFixturesWithDifficulty(team, 5);
          const avgDifficulty = fixtures.length
            ? fixtures.reduce((acc, f) => acc + f.difficulty, 0) / fixtures.length
            : null;
          return { role: roleData.role, purchase, team, fixtures, avgDifficulty };
        })
    );
  }, [myTeam]);

  if (!myTeam || !rows.length) return null;

  return (
    <div className="analysis-stats-table-panel glass">
      <div className="analysis-chart-panel__head">
        <CalendarDays size={16} />
        <div>
          <strong>Calendario della tua rosa</strong>
          <span>Prossime 5 partite e difficoltà per ciascun giocatore di {myTeam.team}</span>
        </div>
      </div>

      <div className="analysis-stats-table__scroll">
        <table className="analysis-stats-table analysis-roster-cal-table">
          <thead>
            <tr>
              <th>Ruolo</th>
              <th>Giocatore</th>
              <th>Prossime 5 partite</th>
              <th>Difficoltà media</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.purchase.playerId || `${row.role}-${getPurchaseName(row.purchase)}`}>
                <td><RoleChip role={row.role} /></td>
                <td>
                  <div className="analysis-stats-table__player analysis-stats-table__player--roster-cal">
                    <TeamBadge team={row.team} size={36} />
                    <div className="analysis-stats-table__playerBody">
                      <span>{getPurchaseName(row.purchase)}</span>
                      <span className="analysis-stats-table__playerCredits">{row.team}</span>
                    </div>
                    <AppTooltip
                      as="button"
                      type="button"
                      className="analysis-stats-table__playerBtn"
                      onClick={() => openPlayerDetailNewTab(row.purchase.playerId)}
                      content="Apri dettaglio giocatore"
                    >
                      <ExternalLink size={12} />
                    </AppTooltip>
                  </div>
                </td>
                <td>
                  {row.fixtures.length ? (
                    <div className="roster-cal-fixtures">
                      {row.fixtures.map((f) => (
                        <AppTooltip
                          as="span"
                          key={f.giornata}
                          className={`roster-cal-fixture ${f.home ? 'is-home' : 'is-away'}`}
                          style={{ '--diff-color': getDifficultyColor(f.difficulty) }}
                          content={`G${f.giornata} · ${formatFixtureDate(f.date)} · ${f.home ? `${row.team}-${f.opponent}` : `${f.opponent}-${row.team}`} · Difficoltà ${formatDifficultyValue(f.difficulty)}/5`}
                        >
                          <TeamBadge team={f.opponent} size={26} showTooltip={false} />
                          <span className="roster-cal-fixture__ha">{f.home ? 'C' : 'T'}</span>
                        </AppTooltip>
                      ))}
                    </div>
                  ) : (
                    <span className="mm-muted">—</span>
                  )}
                </td>
                <td>
                  {row.avgDifficulty != null ? (
                    <strong style={{ color: getDifficultyColor(row.avgDifficulty) }}>
                      {formatDifficultyValue(row.avgDifficulty)} / 5
                    </strong>
                  ) : (
                    <span className="mm-muted">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
